import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import TemplateSelector from './components/TemplateSelector';
import InvitationForm from './components/InvitationForm';
import UploadWidget from './components/UploadWidget';
import GeneratedLinks from './components/GeneratedLinks';
import PaymentResult from './components/PaymentResult';
import InspectWarningModal from './InspectWarningModal';

// Interface for form data
interface FormData {
  groom: string;
  bride: string;
  groomFather: string;
  groomMother: string;
  brideFather: string;
  brideMother: string;
  date: string;
  akadDate: string;
  receptionDate: string;
  akadTime: string;
  receptionTime: string;
  akadLocation: string;
  receptionLocation: string;
  address: string;
  location: string;
  mapLink: string;
  guests: string[];
  facebookLink: string;
  instagramLink: string;
  facebookLink1: string;
  instagramLink1: string;
  userEmail?: string; // Make optional here to match InvitationForm
}

// Component for topbar with company logo and name
const TopBar = () => {
  return (
    <div className="bg-gradient-to-r from-purple-800 to-indigo-900 shadow-md py-3 sticky top-0 z-50">
      <div className="container mx-auto px-4 flex items-center space-x-0">
 <div className="">
                <img 
                  src="https://res.cloudinary.com/dzwdwkiw0/image/upload/v1746344425/cybtw96xsu18neqo2t6f.png" 
                  alt="Rabbit Moon Logo" 
                  className="w-14 h-14 object-contain"
                />
              </div>
              <span className="text-xl text-center align-middle text-white font-bold">Rabbit Moon</span>
            </div>
    </div>
  );
};

// Component for footer with instagram, contact, and company logo
const Footer = () => {
  return (
<footer className="bg-gradient-to-r from-indigo-900 to-purple-800 text-white py-12 shadow-inner">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Company Info */}
          <div className="flex flex-col items-center md:items-start">
            <div className="flex items-center mb-4">
              <div className="">
                <img 
                  src="https://res.cloudinary.com/dzwdwkiw0/image/upload/v1746344425/cybtw96xsu18neqo2t6f.png" 
                  alt="Rabbit Moon Logo" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <span className="text-xl font-bold">Rabbit Moon</span>
            </div>
            <p className="text-gray-200 text-center md:text-left">
              Creating beautiful digital wedding invitations for your special day.
            </p>
            <div className="mt-4 px-6 py-2 bg-purple-700 bg-opacity-40 rounded-lg shadow-md">
              <p className="text-sm text-gray-100 italic">
                "Making your love story unforgettable, one invitation at a time."
              </p>
            </div>
          </div>
          
          {/* Social Media */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-lg font-semibold mb-4 relative">
              <span className="relative z-10">Follow</span>
              <span className="absolute bottom-0 left-0 w-full h-1 bg-pink-400 opacity-50 z-0"></span>
            </h3>
            <div className="flex flex-col space-y-4">
              <a href="https://instagram.com/rabbitmoonwedding" className="hover:text-pink-300 transition-colors duration-300 flex items-center group">
                <div className="w-10 h-10 rounded-full bg-white bg-opacity-10 flex items-center justify-center mr-3 group-hover:bg-pink-500 group-hover:bg-opacity-20 transition-all duration-300">
                  <i className="fab fa-instagram text-lg"></i>
                </div>
                @rabbitmoonwedding
              </a>
            </div>
          </div>
          
          {/* Contact Info */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-lg font-semibold mb-4 relative">
              <span className="relative z-10">Contact</span>
              <span className="absolute bottom-0 left-0 w-full h-1 bg-purple-400 opacity-50 z-0"></span>
            </h3>
            <div className="flex flex-col space-y-4">
              <a href="https://wa.me/6281477182186" target="_blank" rel="noopener noreferrer" className="flex items-center group">
                <div className="w-10 h-10 rounded-full bg-white bg-opacity-10 flex items-center justify-center mr-3 group-hover:bg-green-500 group-hover:bg-opacity-20 transition-all duration-300">
                  <i className="fab fa-whatsapp text-lg"></i>
                </div>
                <span className="group-hover:text-green-400 transition-colors duration-300">+62 814 7718 2186</span>
              </a>

              <a href="mailto:irawanm125@gmail.com" className="flex items-center group">
                <div className="w-10 h-10 rounded-full bg-white bg-opacity-10 flex items-center justify-center mr-3 group-hover:bg-purple-500 group-hover:bg-opacity-20 transition-all duration-300">
                  <i className="far fa-envelope text-lg"></i>
                </div>
                <span className="group-hover:text-purple-300 transition-colors duration-300">irawanm125@gmail.com</span>
              </a>
              <div className="flex items-center group">
                <div className="w-10 h-10 rounded-full bg-white bg-opacity-10 flex items-center justify-center mr-3 group-hover:bg-purple-500 group-hover:bg-opacity-20 transition-all duration-300">
                  <i className="fas fa-map-marker-alt text-lg"></i>
                </div>
                <span className="group-hover:text-purple-300 transition-colors duration-300">Kalimantan Tengah, Indonesia</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Component for step navigation
const StepNavigation = ({ step }: { step: number }) => {
  return (
    <div className="steps">
      <div className={`step ${step >= 1 ? 'active' : ''}`}>1. Select Template</div>
      <div className={`step ${step >= 2 ? 'active' : ''}`}>2. Fill Form</div>
      <div className={`step ${step >= 3 ? 'active' : ''}`}>3. Upload Media</div>
      <div className={`step ${step >= 4 ? 'active' : ''}`}>4. Get Links</div>
    </div>
  );
};

// Main content component
const MainContent = () => {
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templateIsPremium, setTemplateIsPremium] = useState<boolean>(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [photoLink, setPhotoLink] = useState<string>('');
  const [musicLink, setMusicLink] = useState<string>('');
  const [bridePhoto, setBridePhoto] = useState<string>('');
  const [groomPhoto, setGroomPhoto] = useState<string>('');
  const [galleryPhotos, setGalleryPhotos] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData>({
    groom: '',
    bride: '',
    groomFather: '',
    groomMother: '',
    brideFather: '',
    brideMother: '',
    date: '',
    akadDate: '',
    receptionDate: '',
    akadTime: '',
    receptionTime: '',
    akadLocation: '',
    receptionLocation: '',
    address: '',
    location: '',
    mapLink: '',
    guests: [''],
    facebookLink: '',
    instagramLink: '',
    facebookLink1: '',
    instagramLink1: '',
    userEmail: '' // Initialize as empty string
  });
  const [generatedLinks, setGeneratedLinks] = useState<Array<{ guest: string, link: string }>>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const location = useLocation();

  // Handle location state (used when redirecting from payment result)
  useEffect(() => {
    if (location.state) {
      const { orderId } = location.state as { orderId?: string };
      if (orderId) {
        setOrderId(orderId);
      }
    }
  }, [location]);

  // Handle Cloudinary script loading
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Handle file uploads
  const handlePhotoUpload = (url: string) => {
    setPhotoLink(url);
  };

  const handleMusicUpload = (url: string) => {
    setMusicLink(url);
  };

  const handleBridePhotoUpload = (url: string) => {
    setBridePhoto(url);
  };

  const handleGroomPhotoUpload = (url: string) => {
    setGroomPhoto(url);
  };

  const handleGalleryUpload = (url: string) => {
    setGalleryPhotos(prev => [...prev, url]);
  };

  // Template selection (with optional orderId for premium templates)
  const handleTemplateSelect = async (template: string, paymentOrderId?: string) => {
    setSelectedTemplate(template);

    try {
      const response = await fetch(`https://rabbit-moon.up.railway.app/api/template/${template}/info`);
      if (!response.ok) {
        throw new Error(`Server mengembalikan status ${response.status}`);
      }

      const data = await response.json();

      if (!data.success || !data.template) {
        alert('Template tidak ditemukan atau gagal dimuat.');
        return;
      }

      const { isPremium, price } = data.template;
      setTemplateIsPremium(isPremium);

      if (isPremium) {
        if (paymentOrderId) {
          // Verify the payment status before proceeding
          const paymentStatusResponse = await fetch(`https://rabbit-moon.up.railway.app/api/payment-status/${paymentOrderId}`);
          const paymentStatus = await paymentStatusResponse.json();
          
          if (paymentStatus.success && paymentStatus.data.status === 'success') {
            setOrderId(paymentOrderId);
            setStep(2);
          } else {
            alert('Pembayaran belum berhasil. Silakan selesaikan pembayaran terlebih dahulu.');
            // Initiate payment process
            await initiatePayment(template, price);
          }
        } else {
          // No orderId, start payment process
          if (price) {
            await initiatePayment(template, price);
          } else {
            alert('Harga template tidak valid. Silakan coba lagi.');
          }
        }
      } else {
        // For non-premium templates, proceed normally
        setStep(2);
      }
    } catch (err) {
      console.error('Terjadi error saat memilih template:', err);
      alert('Terjadi kesalahan saat memproses template. Silakan coba lagi nanti.');
    }
  };

  const initiatePayment = async (templateId: string, price: number) => {
    try {
      const response = await fetch('https://rabbit-moon.up.railway.app/api/create-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateId,
          price
        }),
      });
      
      const result = await response.json();
      
      if (result.success && result.token) {
        // Store orderId for later use
        setOrderId(result.orderId);
        
        // Redirect to Midtrans payment page
        window.location.href = result.redirectUrl;
      } else {
        alert('Gagal membuat pembayaran: ' + (result.message || 'Terjadi kesalahan'));
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      alert('Terjadi kesalahan saat memproses pembayaran.');
    }
  };

  // Form data handling - Updated to handle optional userEmail
  const handleFormSubmit = (data: FormData) => {
    setFormData(data);
    setStep(3);
  };

  // Upload completion handling
  const handleUploadComplete = () => {
    setIsUploading(false);
    setStep(4);
    generateLinks();
  };

  // Generate invitation links
  const generateLinks = async () => {
    // Display loading status
    setIsUploading(true);
    
    // Check if the template is premium and payment is required
    if (templateIsPremium) {
      if (!orderId) {
        alert('Template ini premium. Harap selesaikan pembayaran terlebih dahulu.');
        setIsUploading(false);
        return;
      }
      
      // Verify payment status before generating links
      try {
        console.log('Memeriksa status pembayaran untuk order:', orderId);
        const paymentStatusResponse = await fetch(`https://rabbit-moon.up.railway.app/api/payment-status/${orderId}`);
        
        if (!paymentStatusResponse.ok) {
          throw new Error(`Gagal memeriksa status pembayaran: ${paymentStatusResponse.status}`);
        }
        
        const paymentStatus = await paymentStatusResponse.json();
        console.log('Respon status pembayaran:', paymentStatus);
        
        if (!paymentStatus.success || paymentStatus.data.status !== 'success') {
          alert('Pembayaran belum berhasil diverifikasi. Silakan selesaikan pembayaran terlebih dahulu.');
          setIsUploading(false);
          return;
        }
      } catch (error) {
        console.error('Error verifikasi pembayaran:', error);
        alert('Gagal memverifikasi status pembayaran: ' + (error as Error).message);
        setIsUploading(false);
        return;
      }
    }

    // Check if the guest list is not empty
    if (!formData.guests || formData.guests.filter(guest => guest.trim() !== '').length === 0) {
      alert('Silakan tambahkan setidaknya satu nama tamu');
      setIsUploading(false);
      return;
    }

    try {
      // Prepare request payload
      const payload = {
        templateId: selectedTemplate,
        orderId: orderId,
        formData: formData,
        mediaData: {
          mainPhoto: photoLink,
          backgroundMusic: musicLink,
          bridePhoto: bridePhoto,
          groomPhoto: groomPhoto,
          galleryPhotos: galleryPhotos,
        },
        userEmail: formData.userEmail || null // Include user email if provided
      };
      
      console.log('Mengirim request generate dengan payload:', JSON.stringify(payload));
      
      const response = await fetch('https://rabbit-moon.up.railway.app/api/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Check if response is OK
      if (!response.ok) {
        throw new Error(`Server merespon dengan status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Respon API generate:', result);

      if (result.success) {
        if (result.links && Array.isArray(result.links) && result.links.length > 0) {
          console.log('Berhasil generate links:', result.links);
          setGeneratedLinks(result.links);
          
          // Show email notification if email was sent
          if (result.emailSent) {
            alert(`Link undangan telah berhasil dikirim ke email: ${result.emailAddress}`);
          }
        } else {
          throw new Error('API mengembalikan success tetapi tidak ada links yang dihasilkan');
        }
      } else {
        throw new Error(result.message || 'Gagal membuat tautan undangan.');
      }
    } catch (err) {
      console.error('Error generating links:', err);
      alert('Terjadi kesalahan saat membuat tautan undangan: ' + (err as Error).message);
    } finally {
      setIsUploading(false);
    }
  };

  // Reset gallery photo
  const removeGalleryPhoto = (index: number) => {
    setGalleryPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Start over - reset everything
  const handleStartOver = () => {
    setStep(1);
    setSelectedTemplate('');
    setTemplateIsPremium(false);
    setOrderId(null);
    setPhotoLink('');
    setMusicLink('');
    setBridePhoto('');
    setGroomPhoto('');
    setGalleryPhotos([]);
    setFormData({
      groom: '',
      bride: '',
      groomFather: '',
      groomMother: '',
      brideFather: '',
      brideMother: '',
      date: '',
      akadDate: '',
      receptionDate: '',
      akadTime: '',
      receptionTime: '',
      akadLocation: '',
      receptionLocation: '',
      address: '',
      location: '',
      mapLink: '',
      guests: [''],
      facebookLink: '',
      instagramLink: '',
      facebookLink1: '',
      instagramLink1: '',
      userEmail: '',
    });
    setGeneratedLinks([]);
  };

  // Render proper step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <TemplateSelector onSelect={handleTemplateSelect} />;
      case 2:
        return (
          <InvitationForm 
            initialData={formData} 
            onSubmit={handleFormSubmit} 
            isPremium={templateIsPremium}
          />
        );
      case 3:
        return (
          <UploadWidget
            onMainPhotoUpload={handlePhotoUpload}
            onMusicUpload={handleMusicUpload}
            onBridePhotoUpload={handleBridePhotoUpload}
            onGroomPhotoUpload={handleGroomPhotoUpload}
            onGalleryUpload={handleGalleryUpload}
            galleryPhotos={galleryPhotos}
            onRemoveGalleryPhoto={removeGalleryPhoto}
            mainPhoto={photoLink}
            musicFile={musicLink}
            bridePhoto={bridePhoto}
            groomPhoto={groomPhoto}
            onComplete={handleUploadComplete}
            setIsUploading={setIsUploading}
            isUploading={isUploading}
          />
        );
      case 4:
        return (
          <GeneratedLinks 
            links={generatedLinks} 
            onStartOver={handleStartOver}
            templateId={selectedTemplate}
          />
        );
      default:
        return <Navigate to="/" />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <StepNavigation step={step} />
      </div>
      
      {renderStepContent()}
      
      {step > 1 && step < 4 && (
        <div className="mt-8 flex justify-between">
          <button
            onClick={() => setStep(prev => Math.max(1, prev - 1))}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
          >
            Kembali
          </button>
          
          {step === 3 && (
            <button
              onClick={handleUploadComplete}
              disabled={isUploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:bg-blue-300"
            >
              {isUploading ? 'Mengunggah...' : 'Selesai'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// App routes
const App = () => {
  return (
    <Router>
      <div 
        className="min-h-screen bg-cover bg-center bg-fixed" 
        style={{ 
           backgroundImage: "url('https://res.cloudinary.com/dzwdwkiw0/image/upload/v1746324964/nxxyinazuhlm1wovof43.png')",
  backgroundSize: "contain",           // agar gambar memenuhi elemen
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
        }}
      >
        <InspectWarningModal />
        
        {/* TopBar Component */}
        <TopBar />
        
        <div className="backdrop-blur-sm bg-white/30 py-6">
          <Routes>
            <Route path="/" element={<MainContent />} />
            <Route path="/payment-result" element={<PaymentResult />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
        
        {/* Footer Component */}
        <Footer />
      </div>
    </Router>
  );
};

export default App;